import React from 'react';
import { titleize } from 'underscore.string';
import i18next, { t } from 'i18next';
import { Checkbox, DataTableColumnHeader } from '@cargomatic/ui';
import { sluggify } from 'utils/localization/utils';
import Badge from 'components/v2/Badge';
import ExternalLink from 'components/v2/ExternalLink';
import { getFormatterForType } from 'components/design-system/molecules/formatters';
/** Resolve `a.b[0].c` style paths against a row object. */
function resolveNestedValue(obj, path) {
    if (!path || !obj)
        return undefined;
    const parts = path.replace(/\[(\d+)\]/g, '.$1').split('.');
    let current = obj;
    for (const part of parts) {
        if (current == null)
            return undefined;
        current = current[part];
    }
    return current;
}
// ---------------------------------------------------------------------------
// Custom cell renderers ported from equipment/Mapping.js. The legacy mapping
// passed `formatter` functions through DataList; here they become the column's
// `cell`. Behaviorally equivalent.
// ---------------------------------------------------------------------------
const CHASSIS_COLOR_MAPPING = {
    customer: 'orange',
    cargomatic: 'green',
};
const equipmentIdFormatter = (cell, row) => {
    // Hide the link entirely when the row has no equipment id (don't render a
    // clickable empty anchor).
    if (cell == null || cell === '')
        return null;
    return (React.createElement("a", { onClick: row.onClick, className: "uppercase break-words cursor-pointer" }, String(cell)));
};
// Entity link cell for the populated `shipment`/`order` refs. The shared
// design-system entity formatter falls back to the row's own `_id` when the
// ref is absent, rendering a bogus link labeled "undefined"; here we only link
// when the populated entity and its display id both exist.
const entityLinkFormatter = (resource, attribute) => function EntityLinkCell(cell) {
    const entity = cell;
    const entityId = entity === null || entity === void 0 ? void 0 : entity._id;
    const label = entity === null || entity === void 0 ? void 0 : entity[attribute];
    if (!entityId || label == null || label === '')
        return null;
    return (React.createElement("div", { className: "flex flex-col items-center justify-center" },
        React.createElement("div", { className: "block overflow-hidden truncate" },
            React.createElement(ExternalLink, { title: String(label), href: `/${resource}/${String(entityId)}`, showIcon: true, dataTestId: `resource-${String(entityId)}` }))));
};
const chassisOwnershipFormatter = (cell) => {
    if (!cell)
        return null;
    const key = String(cell);
    return (React.createElement(Badge, { color: CHASSIS_COLOR_MAPPING[key], size: "large", dataTestId: `chassis-ownership-${key}`, style: {} }, i18next.t(sluggify(key))));
};
/**
 * Build a native TanStack ColumnDef from a spec. The cell reuses the shared
 * design-system formatters (datetime, entity links, …) via getFormatterForType,
 * or a custom `formatter` when supplied.
 */
function dataColumn(spec) {
    var _a, _b, _c, _d;
    const field = (_a = spec.field) !== null && _a !== void 0 ? _a : spec.id;
    const label = titleize(t(spec.slug, { count: (_b = spec.count) !== null && _b !== void 0 ? _b : 1 }));
    const sortable = spec.sortable !== false;
    const formatter = (_c = spec.formatter) !== null && _c !== void 0 ? _c : getFormatterForType({
        type: spec.type,
        entityType: spec.entityType,
        formatExtraData: spec.extra,
    });
    return {
        id: spec.id,
        accessorFn: (row) => resolveNestedValue(row, field),
        header: sortable
            ? ({ column }) => React.createElement(DataTableColumnHeader, { column: column, title: label })
            : () => label,
        cell: ({ row }) => {
            const value = resolveNestedValue(row.original, field);
            const content = spec.formatter
                ? spec.formatter(value, row.original)
                : formatter(value, row.original, row.index, spec.extra);
            return spec.align === 'right' ? (React.createElement("div", { className: "text-right" }, content)) : (content);
        },
        enableSorting: sortable,
        enableHiding: spec.hideable !== false,
        meta: { label, align: (_d = spec.align) !== null && _d !== void 0 ? _d : 'left' },
    };
}
/** TanStack-native checkbox column driven by the table's row-selection state. */
function selectColumn() {
    return {
        id: '__select',
        header: ({ table }) => (React.createElement(Checkbox, { checked: table.getIsAllPageRowsSelected()
                ? true
                : table.getIsSomePageRowsSelected()
                    ? 'indeterminate'
                    : false, onCheckedChange: (value) => table.toggleAllPageRowsSelected(!!value), "aria-label": "Select all" })),
        cell: ({ row }) => (React.createElement(Checkbox, { checked: row.getIsSelected(), onCheckedChange: (value) => row.toggleSelected(!!value), "aria-label": "Select row" })),
        enableSorting: false,
        enableHiding: false,
        size: 40,
    };
}
/**
 * Equipment column specs — 1:1 port of equipment/Mapping.js#columns.
 *
 * Visibility gate matches v1 `List.getColumnList`: every column is
 * user-toggleable in ViewOptions EXCEPT `equipment_id` (the row identity/link
 * column, hideable:false). v1 also excludes `disabled` columns from the toggle
 * list — none of the Mapping columns are flagged `disabled`, so all non-id
 * columns are hideable here. (Disabled-column filtering is enforced once, in
 * EquipmentList's initial hidden set, mirroring v1.)
 */
const EQUIPMENT_SPECS = [
    {
        id: 'equipment_id',
        slug: 'EQUIPMENT_ID',
        formatter: equipmentIdFormatter,
        hideable: false,
    },
    { id: '_equipmentType', slug: 'ASSET_TYPE' },
    {
        id: 'chassis_owner_type',
        slug: 'CHASSIS_OWNERSHIP',
        formatter: chassisOwnershipFormatter,
    },
    { id: '_type', slug: 'EQUIPMENT_TYPE' },
    { id: '_equipmentSize', slug: 'EQUIPMENT_SIZE' },
    { id: 'status', slug: 'STATUS' },
    { id: 'last_free_date', slug: 'FREE', type: 'datetime' },
    { id: 'earliest_empty_date', slug: 'EMPTY', type: 'datetime' },
    { id: 'earliest_return_date', slug: 'RETURN', type: 'datetime' },
    { id: 'last_cut_date', slug: 'CUT', type: 'datetime' },
    {
        id: 'shipment',
        slug: 'SHIPMENT',
        count: 1,
        formatter: entityLinkFormatter('shipments', 'shipment_id'),
    },
    {
        id: 'order',
        slug: 'ORDER',
        formatter: entityLinkFormatter('orders', 'order_id'),
    },
];
/**
 * Full set of column ids that are toggleable via ViewOptions — every column
 * except the always-visible identity column (`equipment_id`). Exposed so the
 * page can seed its hidden-set / persistence logic against the v1 column set.
 */
export const TOGGLEABLE_COLUMN_IDS = EQUIPMENT_SPECS.filter((s) => s.hideable !== false).map((s) => s.id);
/** Specs (sans select column) — used by the page for ViewOptions labels + CSV. */
export const EQUIPMENT_COLUMN_SPECS = EQUIPMENT_SPECS;
export function getEquipmentColumns(opts = {}) {
    var _a;
    const hidden = new Set((_a = opts.hidden) !== null && _a !== void 0 ? _a : []);
    const cols = EQUIPMENT_SPECS.filter((s) => !hidden.has(s.id)).map(dataColumn);
    return opts.selectable ? [selectColumn(), ...cols] : cols;
}
